










































































































import {
  defineComponent,
  onMounted,
  ref,
  computed,
  ComputedRef,
  watch,
} from '@vue/composition-api';
import sl from '@/serviceLocator';
import {
  AddSidePayload,
  CleanLinesPayload,
  EditTextAction,
  EpigraphyResponse,
  LocaleCode,
  RemoveSidePayload,
  EpigraphicUnitSide,
} from '@oare/types';
import { TabletRenderer, createTabletRenderer } from '@oare/oare';
import i18n from '@/i18n';
import SideCard from '@/views/Texts/CollectionTexts/AddTexts/Editor/components/SideCard.vue';
import EditActions from './components/EditActions.vue';
import EditSide from './components/EditSide.vue';
import AddSide from '@/views/Texts/CollectionTexts/AddTexts/Editor/components/AddSide.vue';
import EditSideDialog from './components/EditSideDialog.vue';

export default defineComponent({
  props: {
    textUuid: {
      type: String,
      required: true,
    },
  },
  components: {
    SideCard,
    EditActions,
    EditSide,
    AddSide,
    EditSideDialog,
  },
  setup(props) {
    const server = sl.get('serverProxy');
    const actions = sl.get('globalActions');
    const store = sl.get('store');
    const router = sl.get('router');

    const epigraphyResponseLoading = ref(false);
    const epigraphyResponse = ref<EpigraphyResponse>({
      canWrite: false,
      text: {
        uuid: '',
        type: '',
        name: '',
        excavationPrefix: '',
        excavationNumber: '',
        museumPrefix: '',
        museumNumber: '',
        publicationPrefix: '',
        publicationNumber: '',
      },
      collection: {
        uuid: '',
        name: '',
      },
      cdliNum: '',
      units: [],
      color: '',
      colorMeaning: '',
      discourseUnits: [],
      hasEpigraphy: false,
      zoteroData: [],
    });

    const renderer = ref<TabletRenderer>();

    const resetRenderer = async () => {
      try {
        epigraphyResponseLoading.value = true;
        epigraphyResponse.value = await server.getEpigraphicInfo(
          props.textUuid
        );

        renderer.value = createTabletRenderer(
          epigraphyResponse.value.units,
          i18n.locale as LocaleCode,
          {
            showNullDiscourse: store.getters.isAdmin,
            textFormat: 'html',
          }
        );
      } catch (err) {
        actions.showErrorSnackbar(
          'Error loading text information. Please try again.',
          err as Error
        );
      } finally {
        epigraphyResponseLoading.value = false;
      }
    };

    onMounted(async () => {
      await resetRenderer();
      selectedSide.value = renderer.value!.sides[0].side || 'obv.';
    });

    const selectedSide = ref<EpigraphicUnitSide>();
    const setSide = (side: EpigraphicUnitSide) => {
      selectedSide.value = side;
    };
    const sideTypes: ComputedRef<EpigraphicUnitSide[]> = computed(() => [
      'obv.',
      'lo.e.',
      'rev.',
      'u.e.',
      'le.e.',
      'r.e.',
      'mirror text',
      'legend',
      'suppl. tablet',
      'obv. ii',
    ]);
    const usableSides = computed(() => {
      if (!renderer.value) {
        return [];
      }
      return sideTypes.value.filter(
        type => !renderer.value!.sides.map(side => side.side).includes(type)
      );
    });
    const alreadyUsedSides = computed(() => {
      if (!renderer.value) {
        return [];
      }
      return sideTypes.value.filter(type =>
        renderer.value!.sides.map(side => side.side).includes(type)
      );
    });
    watch(selectedSide, () => {
      if (currentEditAction.value) {
        currentEditAction.value = undefined;
      }
    });

    const currentEditAction = ref<EditTextAction>();

    const editTextLoading = ref(false);

    const addSideDialog = ref(false);
    const sideToAdd = ref<EpigraphicUnitSide>();
    const promptAddSide = (side: EpigraphicUnitSide) => {
      sideToAdd.value = side;
      addSideDialog.value = true;
    };
    const addSide = async () => {
      try {
        editTextLoading.value = true;
        if (!sideToAdd.value) {
          throw new Error('No side to add');
        }
        const payload: AddSidePayload = {
          type: 'addSide',
          side: sideToAdd.value,
          textUuid: props.textUuid,
        };
        await server.editText(payload);
        await resetRenderer();
        selectedSide.value = sideToAdd.value;
      } catch (err) {
        actions.showErrorSnackbar(
          'Error adding side. Please try again.',
          err as Error
        );
      } finally {
        sideToAdd.value = undefined;
        addSideDialog.value = false;
        currentEditAction.value = undefined;
        editTextLoading.value = false;
      }
    };

    const editSideDialog = ref(false);
    watch(currentEditAction, () => {
      if (currentEditAction.value === 'editSide') {
        editSideDialog.value = true;
      }
    });
    watch(editSideDialog, () => {
      if (!editSideDialog.value) {
        currentEditAction.value = undefined;
      }
    });

    const removeSideDialog = ref(false);
    const sideToRemove = ref<EpigraphicUnitSide>();
    const promptRemoveSide = (side: EpigraphicUnitSide) => {
      sideToRemove.value = side;
      removeSideDialog.value = true;
    };
    const removeSide = async () => {
      try {
        editTextLoading.value = true;
        if (!sideToRemove.value) {
          throw new Error('No side to remove');
        }
        const payload: RemoveSidePayload = {
          type: 'removeSide',
          side: sideToRemove.value,
          textUuid: props.textUuid,
        };
        await server.editText(payload);
        await resetRenderer();
        if (selectedSide.value === sideToRemove.value) {
          selectedSide.value =
            renderer.value!.sides[renderer.value!.sides.length - 1].side ||
            undefined;
        }
      } catch (err) {
        actions.showErrorSnackbar(
          'Error removing side. Please try again.',
          err as Error
        );
      } finally {
        sideToRemove.value = undefined;
        removeSideDialog.value = false;
        currentEditAction.value = undefined;
        editTextLoading.value = false;
      }
    };

    const cleanLinesDialog = ref(false);
    const cleanLines = async () => {
      try {
        editTextLoading.value = true;
        const payload: CleanLinesPayload = {
          type: 'cleanLine',
          textUuid: props.textUuid,
        };
        await server.editText(payload);
        await resetRenderer();
      } catch (err) {
        actions.showErrorSnackbar(
          'Error cleaning lines. Please try again.',
          err as Error
        );
      } finally {
        cleanLinesDialog.value = false;
        currentEditAction.value = undefined;
        editTextLoading.value = false;
      }
    };
    watch(currentEditAction, () => {
      if (currentEditAction.value === 'cleanLine') {
        cleanLinesDialog.value = true;
      }
    });
    watch(cleanLinesDialog, () => {
      if (!cleanLinesDialog.value) {
        currentEditAction.value = undefined;
      }
    });

    const closeEditor = () => {
      router.push(`/epigraphies/${props.textUuid}`);
    };

    return {
      epigraphyResponse,
      epigraphyResponseLoading,
      renderer,
      selectedSide,
      setSide,
      usableSides,
      currentEditAction,
      promptAddSide,
      addSideDialog,
      sideToAdd,
      addSide,
      editTextLoading,
      resetRenderer,
      closeEditor,
      alreadyUsedSides,
      removeSideDialog,
      sideToRemove,
      promptRemoveSide,
      removeSide,
      cleanLinesDialog,
      cleanLines,
      editSideDialog,
    };
  },
});
